// Copyright 2021 The Prometheus Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { SyntaxNode } from '@lezer/common';
import {
  Add,
  AggregateExpr,
  And,
  BinaryExpr,
  BoolModifier,
  Div,
  Duration,
  Eql,
  EqlRegex,
  EqlSingle,
  FunctionCallBody,
  GroupingLabels,
  Gte,
  Gtr,
  Identifier,
  LabelMatcher,
  LabelMatchers,
  LabelName,
  Lss,
  Lte,
  MatchOp,
  MatrixSelector,
  Mod,
  Mul,
  Neq,
  NeqRegex,
  NumberLiteral,
  OffsetExpr,
  Or,
  Pow,
  PromQL,
  QuotedLabelName,
  StepInvariantExpr,
  StringLiteral,
  Sub,
  SubqueryExpr,
  TemplateVariable,
  Unless,
  VectorSelector,
} from '../../lezer/parser.terms';
import { PrometheusClient } from '../client';
import {
  buildLabelMatchers,
  containsAtLeastOneChild,
  containsChild,
  getMetricName,
  getMetricNameFromLabelMatchers,
  getMetricNameInVectorSelector,
  labelNameHasUTF8Characters,
  metricHasUTF8Characters,
  quoteString,
  walkBackward,
} from '../parser';
import { Matcher } from '../types';
import { getMetadataTooltipDOM } from '../utils/promql-metadata-tooltip-content';
import { CompleteStrategy, VariableCompletion } from './index';
import {
  aggregateOpModifierTerms,
  aggregateOpTerms,
  atModifierTerms,
  binOpModifierTerms,
  binOpTerms,
  builtInVariableTerms,
  durationTerms,
  functionIdentifierTerms,
  matchOpTerms,
  numberTerms,
  snippets,
} from './promql.terms';

const autocompleteNodes: { [key: string]: Completion[] } = {
  matchOp: matchOpTerms,
  binOp: binOpTerms,
  duration: durationTerms,
  binOpModifier: binOpModifierTerms,
  atModifier: atModifierTerms,
  functionIdentifier: functionIdentifierTerms,
  aggregateOp: aggregateOpTerms,
  aggregateOpModifier: aggregateOpModifierTerms,
  number: numberTerms,
};

const durationVariablePrefixStopChars = new Set(['[', ':']);
const labelValueVariablePrefixStopChars = new Set(['"', "'", '`', ' ', '\t', ',', '}']);

function normalizeVariableName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function normalizeLabelName(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  const unquoted =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith('`') && trimmed.endsWith('`')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
      ? trimmed.slice(1, -1)
      : trimmed;
  return normalizeVariableName(unquoted);
}

function compareVariableCompletions(a: VariableCompletion, b: VariableCompletion): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function isLabelValueVariable(variable: VariableCompletion): boolean {
  return variable.kind === undefined || variable.kind === 'label' || variable.kind === 'text';
}

function isDurationVariable(variable: VariableCompletion): boolean {
  return variable.kind === 'interval' || variable.kind === 'range';
}

function isRegexMatchOperator(labelMatcher?: SyntaxNode | null): boolean {
  const matchOp = labelMatcher?.getChild(MatchOp)?.firstChild;
  return matchOp?.type.id === EqlRegex || matchOp?.type.id === NeqRegex;
}

function unquoteLabelName(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (trimmed.length < 2) {
    return trimmed;
  }
  const quote = trimmed[0];
  const isQuoted = (quote === '"' || quote === "'" || quote === '`') && trimmed[trimmed.length - 1] === quote;
  return isQuoted ? trimmed.slice(1, -1) : trimmed;
}

function findLabelValueEndAfterMatchOp(state: EditorState, to: number): number {
  let valueStart = to;
  while (/\s/.test(state.sliceDoc(valueStart, valueStart + 1))) {
    valueStart++;
  }
  if (state.sliceDoc(valueStart, valueStart + 1) === '=') {
    valueStart++;
    while (/\s/.test(state.sliceDoc(valueStart, valueStart + 1))) {
      valueStart++;
    }
  }
  if (state.sliceDoc(valueStart, valueStart + 1) === '~') {
    valueStart++;
    while (/\s/.test(state.sliceDoc(valueStart, valueStart + 1))) {
      valueStart++;
    }
  }

  const quote = state.sliceDoc(valueStart, valueStart + 1);
  if (quote !== '"' && quote !== "'" && quote !== '`') {
    return to;
  }

  for (let index = valueStart + 1; index < state.doc.length; index++) {
    const char = state.sliceDoc(index, index + 1);
    if (char === quote && (quote === '`' || state.sliceDoc(index - 1, index) !== '\\')) {
      return index + 1;
    }
  }

  return to;
}

function findMatchOpStart(state: EditorState, from: number): number {
  let start = from;
  while (start > 0) {
    const previousChar = state.sliceDoc(start - 1, start);
    if (previousChar !== '=' && previousChar !== '~' && previousChar !== '!') {
      break;
    }
    start--;
  }
  return start;
}

function findMatchOpEnd(state: EditorState, to: number): number {
  let end = to;
  while (end < state.doc.length) {
    const nextChar = state.sliceDoc(end, end + 1);
    if (nextChar !== '=' && nextChar !== '~' && nextChar !== '!') {
      break;
    }
    end++;
  }
  return end;
}

// ContextKind is the different possible value determinate by the autocompletion
export enum ContextKind {
  // dynamic autocompletion (required a distant server)
  MetricName,
  LabelName,
  LabelValue,
  // static autocompletion
  Function,
  Aggregation,
  BinOpModifier,
  BinOp,
  MatchOp,
  AggregateOpModifier,
  Duration,
  Offset,
  Bool,
  AtModifiers,
  Number,
}

export interface Context {
  kind: ContextKind;
  metricName?: string;
  labelName?: string;
  matchers?: Matcher[];
  regexLabelValue?: boolean;
  /**
   * True if the context is within quotes (StringLiteral or QuotedLabelName).
   */
  quoted?: boolean;
  /**
   * Only applicable to MetricName kind.
   */
  insideLabelMatchers?: boolean;
  /**
   * Optional override for the autocomplete start position.
   */
  startPos?: number;
  /**
   * Hide metrics with the UTF-8 characters introduced by Prometheus 3.0.
   * These characters include dots, dashes, and slashes, e.g. `my.metric`, `my-metric`, `my/metric`.
   *
   * https://prometheus.io/docs/guides/utf8/#querying
   */
  shouldHideUTF8Metrics?: boolean;
}

function getExistingGroupingLabels(tree: SyntaxNode, state: EditorState): string[] {
  // Extract unique label names that are already present in the GroupingLabels
  // For example, in "sum by (instance, job, `instance`)", returns ["instance", "job"]
  const groupingLabelsNode: SyntaxNode | null = walkBackward(tree, GroupingLabels);
  if (!groupingLabelsNode) {
    return [];
  }

  const existingLabelsSet = new Set<string>();
  groupingLabelsNode.cursor().iterate((node) => {
    if (node.type.id === LabelName) {
      existingLabelsSet.add(state.sliceDoc(node.from, node.to));
    } else if (node.type.id === QuotedLabelName) {
      // Remove the quotes and add to set (deduplicates quoted and unquoted versions)
      const labelName = state.sliceDoc(node.from, node.to).slice(1, -1);
      existingLabelsSet.add(labelName);
    }
  });

  return Array.from(existingLabelsSet);
}

function getMetricContextInGroupBy(tree: SyntaxNode, state: EditorState): { metricName: string; matchers?: Matcher[] } {
  // There should be an AggregateExpr as parent of the GroupingLabels.
  // Then we should find the VectorSelector child to be able to find the metric name and matchers.
  const currentNode: SyntaxNode | null = walkBackward(tree, AggregateExpr);
  if (!currentNode) {
    return { metricName: '' };
  }
  let metricName = '';
  let matchers: Matcher[] = [];
  currentNode.cursor().iterate((node) => {
    // Continue until we find the VectorSelector, then look up the metric name and matchers.
    if (node.type.id === VectorSelector) {
      const vectorSelector = node.node;
      metricName = getMetricNameInVectorSelector(vectorSelector, state);

      // Also extract the label matchers from the VectorSelector
      const labelMatchersNode = vectorSelector.getChild(LabelMatchers);
      if (labelMatchersNode) {
        matchers = buildLabelMatchers(labelMatchersNode.getChildren(LabelMatcher), state);

        // If no metric name from Identifier, try to get it from LabelMatchers (e.g., {"metric_name"})
        if (!metricName) {
          metricName = getMetricNameFromLabelMatchers(labelMatchersNode, state);
        }
      }

      if (metricName || matchers.length > 0) {
        return false;
      }
    }
  });

  // Also add the existing grouping labels as "pseudo-matchers" so they get filtered out
  const existingGroupingLabels = getExistingGroupingLabels(tree, state);
  for (const labelName of existingGroupingLabels) {
    // Create a fake matcher to represent already-used grouping labels
    matchers.push(new Matcher(0, labelName, ''));
  }

  return { metricName, matchers: matchers.length > 0 ? matchers : undefined };
}

function arrayToCompletionResult(
  data: Completion[],
  from: number,
  to: number,
  includeSnippet = false,
  span = true,
  filter = true
): CompletionResult {
  const options = data;
  if (includeSnippet) {
    options.push(...snippets);
  }
  const result: CompletionResult = {
    from: from,
    to: to,
    options: options,
    // If not in free-form field like label value, cover Datadog/OTel/Prometheus character sets
    // that don't overlap with MatchOp.
    // OTel technically allows any instrument names (spec says SHOULD), but it'd add a lot of
    // complexity, if it overlapped with matchers.
    validFor: span ? /^[a-zA-Z0-9_/:.-]+$/ : undefined,
  };
  if (!filter) {
    result.filter = false;
  }
  return result;
}

// computeStartCompleteLabelPositionInLabelMatcherOrInGroupingLabel calculates the start position only when the node is a LabelMatchers or a GroupingLabels
function computeStartCompleteLabelPositionInLabelMatcherOrInGroupingLabel(node: SyntaxNode, pos: number): number {
  // Here we can have two different situations:
  // 1. `metric{}` or `sum by()` with the cursor between the bracket
  // and so we have increment the starting position to avoid to consider the open bracket when filtering the autocompletion list.
  // 2. `metric{foo="bar",} or `sum by(foo,)  with the cursor after the comma.
  // Then the start number should be the current position to avoid to consider the previous labelMatcher/groupingLabel when filtering the autocompletion list.
  let start = node.from + 1;
  if (node.firstChild !== null) {
    // here that means the LabelMatchers / GroupingLabels has a child, which is not possible if we have the expression `metric{}`. So we are likely trying to autocomplete the label list after a comma
    start = pos;
  }
  return start;
}

// computeStartCompletePosition calculates the start position of the autocompletion.
// It is an important step because the start position will be used by CMN to find the string and then to use it to filter the CompletionResult.
// A wrong `start` position will lead to have the completion not working.
// Note: this method is exported only for testing purpose.
export function computeStartCompletePosition(node: SyntaxNode, pos: number): number {
  let start = node.from;
  if (node.type.id === LabelMatchers || node.type.id === GroupingLabels) {
    start = computeStartCompleteLabelPositionInLabelMatcherOrInGroupingLabel(node, pos);
  } else if (
    node.type.id === FunctionCallBody ||
    (node.type.id === StringLiteral && node.parent?.type.id === LabelMatcher) ||
    (node.type.id === QuotedLabelName && node.parent?.type.id === LabelMatchers) ||
    (node.type.id === QuotedLabelName && node.parent?.type.id === GroupingLabels)
  ) {
    // When the cursor is between bracket, quote, we need to increment the starting position to avoid to consider the open bracket/ first string.
    start++;
  } else if (
    node.type.id === OffsetExpr ||
    (node.type.id === NumberLiteral && node.parent?.type.isError && node.parent.parent?.type.id === SubqueryExpr) ||
    (node.type.isError &&
      (node.parent?.type.id === OffsetExpr ||
        node.parent?.type.id === MatrixSelector ||
        (node.parent?.type.id === SubqueryExpr && containsAtLeastOneChild(node.parent, Duration))))
  ) {
    start = pos;
  }
  return start;
}

/**
 * `analyzeNode` is going to determine what should be in the list of suggestions.
 * It returns an array of `Context` objects, where each `Context` is a kind of suggestion (e.g. `MetricName`, `LabelName`, `LabelValue`, etc.).
 *
 * Note: This method is exported for testing purposes only. Do not use it directly.
 */
export function analyzeNode(state: EditorState, node: SyntaxNode): Context[] {
  const result: Context[] = [];
  switch (node.type.id) {
    case 0: // Error node
      if (node.parent?.type.id === OffsetExpr) {
        // we are likely in the given situation:
        // `metric_name offset 5` that leads to this tree:
        // `OffsetExpr(VectorSelector(Identifier),Offset,⚠)`
        // Here we can just autocomplete a duration.
        result.push({ kind: ContextKind.Duration });
        break;
      }
      if (node.parent?.type.id === LabelMatcher || node.parent?.type.id === LabelMatchers) {
        // If parent is LabelMatchers (not LabelMatcher), check if we should suggest metric names
        // This handles typing {alertmanager.alerts} where the dot breaks the identifier
        if (node.parent?.type.id === LabelMatchers) {
          const labelMatchersNode = node.parent;
          const errorMetricName = getMetricName(labelMatchersNode, state);
          if (!errorMetricName) {
            // Capture the partial metric name before the error (e.g., "alertmanager." in "{alertmanager.}")
            let startPos = labelMatchersNode.from + 1; // Position after '{'
            const fullContent = state.sliceDoc(startPos, labelMatchersNode.to - 1);

            // Find the last comma to get the start of current matcher
            const lastCommaIndex = fullContent.lastIndexOf(',', node.from - startPos);
            if (lastCommaIndex >= 0) {
              startPos = labelMatchersNode.from + 1 + lastCommaIndex + 1;
            }

            // Get text from start to end of error node (includes the dot), then trim
            // This gives us "alertmanager." when cursor is after the dot in "{alertmanager.}"
            const partialText = state.sliceDoc(startPos, node.to).trim();

            // Adjust startPos if there's leading whitespace that was trimmed
            const actualStart = state.sliceDoc(startPos, node.to).indexOf(partialText) + startPos;

            result.push({
              kind: ContextKind.MetricName,
              metricName: partialText,
              quoted: false,
              startPos: actualStart,
              insideLabelMatchers: true,
            });

            // Only suggest metric names, not match operators
            break;
          }
        }

        // In this case the current token is not itself a valid match op yet:
        //      metric_name{labelName!}
        result.push({ kind: ContextKind.MatchOp });
        break;
      }
      if (node.parent?.type.id === MatrixSelector) {
        // we are likely in the given situation:
        // `metric_name{}[5]`
        // We can also just autocomplete a duration
        result.push({ kind: ContextKind.Duration });
        break;
      }
      if (node.parent?.type.id === SubqueryExpr && containsAtLeastOneChild(node.parent, Duration)) {
        // we are likely in the given situation:
        //    `rate(foo[5d:5])`
        // so we should autocomplete a duration
        result.push({ kind: ContextKind.Duration });
        break;
      }

      if (
        node.parent?.type.id === VectorSelector &&
        (node.prevSibling?.type.id === Identifier || node.prevSibling?.type.isError) &&
        node.nextSibling?.type.id === LabelMatchers
      ) {
        // Example: chrono.[cursor here]{...}
        // Example: chrono.probe.[cursor here]{...}
        // In these cases, the user is typing invalid PromQL.
        // We don't want to suggest any metrics, because we will not be able to autocomplete these metrics.
        break;
      }

      // when we are in the situation 'metric_name !', we have the following tree
      // VectorSelector(Identifier,⚠)
      // We should try to know if the char '!' is part of a binOp.
      // Note: as it is quite experimental, maybe it requires more condition and to check the current tree (parent, other child at the same level ..etc.).
      const operator = state.sliceDoc(node.from, node.to);
      if (binOpTerms.filter((term) => term.label.includes(operator)).length > 0) {
        result.push({ kind: ContextKind.BinOp });
      }

      // TODO: I think we can get rid of this now?
      const startPos = node.parent?.from || 0;
      const fullText = node.parent ? state.sliceDoc(node.parent.from, node.to).trim() : '';

      if (!fullText.includes(' ')) {
        result.push({
          kind: ContextKind.MetricName,
          metricName: fullText,
          quoted: false,
          startPos: startPos,
        });
      }
      break;
    case Identifier:
      // Sometimes an Identifier has an error parent. This should be treated in priority.
      if (node.parent?.type.isError) {
        const errorNodeParent = node.parent.parent;
        if (errorNodeParent?.type.id === StepInvariantExpr) {
          // we are likely in the given situation:
          //   `expr @ s`
          // we can autocomplete start / end
          result.push({ kind: ContextKind.AtModifiers });
          break;
        }
        if (errorNodeParent?.type.id === AggregateExpr) {
          // it matches 'sum() b'. So here we can autocomplete:
          // - the aggregate operation modifier
          // - the binary operation (since it's not mandatory to have an aggregate operation modifier)
          result.push({ kind: ContextKind.AggregateOpModifier }, { kind: ContextKind.BinOp });
          break;
        }
        if (errorNodeParent?.type.id === VectorSelector) {
          if (node.parent.nextSibling?.type.id === LabelMatchers) {
            // Example: chrono.[cursor here]{...}
            // In this case, we do not want to show any metric suggestions,
            // because we cannot autocomplete these metrics.
            break;
          }

          // it matches 'sum b'. So here we also have to autocomplete the aggregate operation modifier only
          // if the associated identifier is matching an aggregation operation.
          // Note: here is the corresponding tree in order to understand the situation:
          // VectorSelector(
          //   Identifier,
          //   ⚠(Identifier)
          // )
          const operator = getMetricNameInVectorSelector(node, state);
          if (aggregateOpTerms.filter((term) => term.label === operator).length > 0) {
            result.push({ kind: ContextKind.AggregateOpModifier });
          }

          // It's possible it also matches the expr 'metric_name unle'.
          // It's also possible that the operator is also a metric even if it matches the list of aggregation function.
          // So we also have to autocomplete the binary operator.
          // The expr `metric_name off` leads to the same tree. So we have to provide the offset keyword too here.
          result.push({ kind: ContextKind.BinOp }, { kind: ContextKind.Offset });

          const fullText = state.sliceDoc(errorNodeParent.from, node.to).trim();
          const hasSpaces = fullText.includes(' ');
          if (metricHasUTF8Characters(fullText) && !hasSpaces) {
            // Example: chrono.probe[cursor here]
            // This appears to be a metric with non-legacy characters.
            // In this case, we suggest all metrics because we CAN autocomplete all metrics.
            // For example, if the user selects `chrono.probe.otel`, we will autocomplete it with `{"chrono.probe.otel"}`.
            result.push({
              kind: ContextKind.MetricName,
              metricName: fullText,
              quoted: false,
              startPos: errorNodeParent.from,
            });
          }
          break;
        }
        // Special case: Identifier directly inside LabelMatchers (e.g., {instance="foo",alertmanager})
        // LabelMatchers(LabelMatcher,⚠(Identifier))
        // This happens when typing a metric name inside braces with existing matchers
        if (errorNodeParent?.type.id === LabelMatcher) {
          let existingMetricName = '';
          if (errorNodeParent.parent?.type.id == LabelMatchers) {
            existingMetricName = getMetricName(errorNodeParent.parent, state);
          }
          const fullContent = state.sliceDoc(errorNodeParent.from, node.to).trim();
          const actualStart = errorNodeParent.from;
          if (!existingMetricName) {
            // No metric name yet, suggest both label names and metric names
            // To capture the full metric name like "alertmanager.a" instead of just "a",
            // we need to find the start of this identifier sequence
            // Walk backwards from node.from to find '{' or ',' which marks the start
            result.push({ kind: ContextKind.LabelName });
            result.push({
              kind: ContextKind.MetricName,
              metricName: fullContent,
              quoted: false,
              startPos: actualStart,
              insideLabelMatchers: true,
            });
          } else {
            result.push({ kind: ContextKind.LabelName, metricName: existingMetricName });
          }
          break;
        }
        if (errorNodeParent && containsChild(errorNodeParent, 'Expr')) {
          // this last case can appear with the following expression:
          // 1. http_requests_total{method="GET"} off
          // 2. rate(foo[5m]) un
          // 3. sum(http_requests_total{method="GET"} off)
          // For these different cases we have this kind of tree:
          // Parent (
          //    ⚠(Identifier)
          // )
          // We don't really care about the parent, here we are more interested if in the siblings of the error node, there is the node 'Expr'
          // If it is the case, then likely we should autocomplete the BinOp or the offset.
          result.push({ kind: ContextKind.BinOp }, { kind: ContextKind.Offset });
          break;
        }
      }
      // As the leaf Identifier is coming for different cases, we have to take a bit time to analyze the tree
      // in order to know what we have to autocomplete exactly.
      // Here is some cases:
      // 1. metric_name / ignor --> we should autocomplete the BinOpModifier + metric/function/aggregation
      // 2. sum(http_requests_total{method="GET"} / o) --> BinOpModifier + metric/function/aggregation
      // Examples above give a different tree each time and ends up to be treated in this case.
      // But they all have the following common tree pattern:
      // Parent( ...,
      //         ... ,
      //         VectorSelector(Identifier)
      //       )
      //
      // So the first things to do is to get the `Parent` and to determinate if we are in this configuration.
      // Otherwise we would just have to autocomplete the metric / function / aggregation.

      const parent = node.parent?.parent;
      if (!parent) {
        // this case can be possible if the topNode is not anymore PromQL but MetricName.
        // In this particular case, then we just want to autocomplete the metric
        result.push({
          kind: ContextKind.MetricName,
          metricName: state.sliceDoc(node.from, node.to),
        });
        break;
      }
      // now we have to know if we have two Expr in the direct children of the `parent`
      const containExprTwice = containsChild(parent, 'Expr', 'Expr');
      if (containExprTwice) {
        if (parent.type.id === BinaryExpr && !containsAtLeastOneChild(parent, 0)) {
          // We are likely in the case 1 or 5
          result.push(
            { kind: ContextKind.MetricName, metricName: state.sliceDoc(node.from, node.to) },
            { kind: ContextKind.Function },
            { kind: ContextKind.Aggregation },
            { kind: ContextKind.BinOpModifier },
            { kind: ContextKind.Number }
          );
          // in  case the BinaryExpr is a comparison, we should autocomplete the `bool` keyword. But only if it is not present.
          // When the `bool` keyword is NOT present, then the expression looks like this:
          // 			BinaryExpr( ..., Gtr , ... )
          // When the `bool` keyword is present, then the expression looks like this:
          //      BinaryExpr( ..., Gtr , BoolModifier(...), ... )
          if (
            containsAtLeastOneChild(parent, Eql, Gte, Gtr, Lte, Lss, Neq) &&
            !containsAtLeastOneChild(parent, BoolModifier)
          ) {
            result.push({ kind: ContextKind.Bool });
          }
        }
        break;
      }

      if (node.nextSibling?.type.id === LabelMatchers) {
        // Example: chrono[cursor here]{...}
        // In this case, we want to suggest metrics, but we don't want to suggest UTF-8 metrics.
        result.push({
          kind: ContextKind.MetricName,
          metricName: state.sliceDoc(node.from, node.to),
          shouldHideUTF8Metrics: true,
        });
        break;
      } else {
        result.push(
          { kind: ContextKind.MetricName, metricName: state.sliceDoc(node.from, node.to) },
          { kind: ContextKind.Function },
          { kind: ContextKind.Aggregation }
        );
        if (parent.type.id !== FunctionCallBody && parent.type.id !== MatrixSelector) {
          // it's too avoid to autocomplete a number in situation where it shouldn't.
          // Like with `sum by(rat)`
          result.push({ kind: ContextKind.Number });
        }
      }
      break;
    case PromQL:
      if (node.firstChild !== null && node.firstChild.type.isError) {
        // In this case, the query is empty.
        // User triggered autocomplete with `Ctrl + Space`.
        result.push(
          { kind: ContextKind.MetricName },
          { kind: ContextKind.Function },
          { kind: ContextKind.Aggregation },
          { kind: ContextKind.Number }
        );
        break;
      }

      if (
        node.firstChild !== null &&
        node.firstChild.type.id === VectorSelector &&
        node.firstChild.firstChild?.type.id === LabelMatchers
      ) {
        // Example: [cursor here]{...} + user triggered autocomplete with `Ctrl + Space`
        // In this case, we want to suggest metrics, but we don't want to suggest UTF-8 metrics.
        result.push({ kind: ContextKind.MetricName, shouldHideUTF8Metrics: true });
        break;
      }

      break;
    case GroupingLabels:
      // In this case we are in the given situation:
      //      sum by () or sum (metric_name{...}) by ()
      // so we have or to autocomplete any kind of labelName or to autocomplete only the labelName associated to the metric
      // Also pass along any matchers from the VectorSelector to enable more accurate label suggestions
      const groupByContext = getMetricContextInGroupBy(node, state);
      result.push({
        kind: ContextKind.LabelName,
        metricName: groupByContext.metricName,
        matchers: groupByContext.matchers,
      });
      break;
    case LabelMatchers:
      // In that case we are in the given situation:
      //       metric_name{} or {}
      // If there's no metric name yet, suggest both label names AND metric names
      // (to support both {instance=""} and {myLabel="value","metric.name"})
      const matchers = buildLabelMatchers(node.getChildren(LabelMatcher), state);
      const labelMatchersMetricName = getMetricName(node, state);

      // Always suggest label names
      result.push({
        kind: ContextKind.LabelName,
        metricName: labelMatchersMetricName || undefined,
        matchers: matchers.length > 0 ? matchers : undefined,
      });

      // If there's no metric name yet, also suggest metric names (for cases like {alertmanager.alerts})
      // Metric names can appear anywhere in the matcher list, e.g., {myLabel="value","metric.name"}
      if (!labelMatchersMetricName) {
        result.push({
          kind: ContextKind.MetricName,
          quoted: false, // Not in quoted context, so auto-quoting will apply
          insideLabelMatchers: true,
        });
      }
      break;
    case LabelName:
      if (node.parent?.type.id === GroupingLabels) {
        // Example: sum by (my_string[cursor here])
        const groupByContextForLabelName = getMetricContextInGroupBy(node, state);
        result.push({
          kind: ContextKind.LabelName,
          metricName: groupByContextForLabelName.metricName,
          matchers: groupByContextForLabelName.matchers,
        });
      } else if (node.parent?.type.id === LabelMatcher) {
        // Example: metric{my_string[cursor here]} or {my_string[cursor here]}
        const metricName = getMetricName(node, state);
        if (!metricName) {
          result.push({
            kind: ContextKind.MetricName,
            quoted: false,
            insideLabelMatchers: true,
          });
        }
        const matcherNode = walkBackward(node, LabelMatchers);
        const matchers = buildLabelMatchers(matcherNode ? matcherNode.getChildren(LabelMatcher) : [], state);
        result.push({
          kind: ContextKind.LabelName,
          metricName: metricName,
          matchers: matchers.length > 0 ? matchers : undefined,
        });
      }
      break;
    case QuotedLabelName:
      const metricNameFromLabelMatchers = getMetricNameFromLabelMatchers(node, state);
      const stringValue = state.sliceDoc(node.from, node.to).slice(1, -1);
      switch (node.parent?.type.id) {
        case GroupingLabels:
          const groupByContextForQuoted = getMetricContextInGroupBy(node, state);
          result.push({
            kind: ContextKind.LabelName,
            quoted: true,
            labelName: stringValue,
            metricName: groupByContextForQuoted.metricName,
            matchers: groupByContextForQuoted.matchers,
          });
          break;
        case LabelMatcher:
          let labelName = '';
          if (node.parent?.firstChild?.type.id === LabelName) {
            labelName = state.sliceDoc(node.parent.firstChild.from, node.parent.firstChild.to);
          }
          result.push({
            kind: ContextKind.LabelValue,
            labelName: labelName,
            metricName: metricNameFromLabelMatchers || undefined,
            regexLabelValue: isRegexMatchOperator(node.parent) || undefined,
          });
          break;
        case LabelMatchers:
          // Example: {"[cursor here]"}
          const metricNameFromIdentifier = getMetricNameInVectorSelector(node, state);
          const metricName = getMetricName(node, state) || undefined;
          const hasOtherMetricName = metricNameFromLabelMatchers && metricNameFromLabelMatchers !== stringValue;
          if (!metricNameFromIdentifier && !hasOtherMetricName) {
            // In this case, user could be looking for a metric or a label.
            // We'll suggest both.
            result.push({
              kind: ContextKind.MetricName,
              metricName: metricName,
              quoted: true,
              insideLabelMatchers: true,
            });
          }
          const childNodes = node ? node.parent.getChildren(LabelMatcher) : [];
          const labelMatchers = buildLabelMatchers(childNodes, state);
          result.push({
            kind: ContextKind.LabelName,
            quoted: true,
            labelName: stringValue,
            matchers: labelMatchers.length > 0 ? labelMatchers : undefined,
            metricName: metricName,
          });
          break;
      }
      break;
    case StringLiteral:
      const nodeParentType = node.parent?.type.id;
      if (nodeParentType === LabelMatcher || nodeParentType === LabelMatchers) {
        // In this case we are in the given situation:
        //      metric_name{labelName=""}
        // So we can autocomplete the labelValue

        // Get the labelName.
        // By definition it's the firstChild: https://github.com/promlabs/lezer-promql/blob/0ef65e196a8db6a989ff3877d57fd0447d70e971/src/promql.grammar#L250
        let labelName = '';
        if (node.parent?.firstChild?.type.id === LabelName) {
          labelName = state.sliceDoc(node.parent.firstChild.from, node.parent.firstChild.to);
        } else if (node.parent?.firstChild?.type.id === QuotedLabelName) {
          labelName = state.sliceDoc(node.parent.firstChild.from, node.parent.firstChild.to).slice(1, -1);
        }

        // Check if there's a QuotedLabelName sibling before this StringLiteral
        // This handles cases like {env="demo","alertmanager_alerts",instance=""}
        // where "instance" might not be wrapped in a LabelMatcher
        if (!labelName && nodeParentType === LabelMatchers) {
          let prevSibling = node.prevSibling;
          // Skip MatchOp if present
          if (prevSibling?.type.id === MatchOp) {
            prevSibling = prevSibling.prevSibling;
          }
          if (prevSibling?.type.id === QuotedLabelName) {
            labelName = state.sliceDoc(prevSibling.from, prevSibling.to).slice(1, -1);
          }
        }
        // then find the metricName if it exists (either unquoted or quoted)
        const metricName = getMetricName(node, state);
        // finally get the full matcher available
        const matcherNode = walkBackward(node, LabelMatchers);
        const labelMatchers = buildLabelMatchers(matcherNode ? matcherNode.getChildren(LabelMatcher) : [], state);

        // Remove the current value from the labelValue since we don't want to filter the list of labelValues by the current value
        const strValue = state.sliceDoc(node.from, node.to).slice(1, -1);
        const labelMatchersWithoutCurrentValue = labelMatchers.filter((matcher) => matcher.value !== strValue);
        // If we found a labelName, this is a LabelValue context
        // Otherwise, if parent is LabelMatchers and no labelName, this is a MetricName
        if (labelName) {
          result.push({
            kind: ContextKind.LabelValue,
            metricName: metricName,
            labelName: labelName,
            matchers: labelMatchersWithoutCurrentValue,
            regexLabelValue: isRegexMatchOperator(node.parent) || undefined,
          });
        } else if (nodeParentType === LabelMatchers) {
          result.push({
            kind: ContextKind.MetricName,
            metricName: strValue,
            quoted: true,
            insideLabelMatchers: true,
            matchers: labelMatchersWithoutCurrentValue,
          });
        } else {
          result.push({
            kind: ContextKind.LabelValue,
            metricName: metricName,
            labelName: labelName,
            matchers: labelMatchersWithoutCurrentValue,
          });
        }
      }
      break;
    case NumberLiteral:
      if (node.parent?.type.isError && node.parent.parent?.type.id === SubqueryExpr) {
        // Here we are likely in this situation:
        //     `go[5d:4]`
        // and we have the given tree:
        // SubqueryExpr(
        //   VectorSelector(Identifier),
        //   Duration, Duration, ⚠(NumberLiteral)
        // )
        // So we should continue to autocomplete a duration
        result.push({ kind: ContextKind.Duration });
      } else {
        result.push({ kind: ContextKind.Number });
      }
      break;
    case Duration:
    case OffsetExpr:
      result.push({ kind: ContextKind.Duration });
      break;
    case TemplateVariable:
      // When cursor is inside a template variable (e.g., $__rate_interval) within a MatrixSelector or SubqueryExpr,
      // provide Duration context to allow variable autocompletion
      if (node.parent?.type.id === MatrixSelector || node.parent?.type.id === SubqueryExpr) {
        result.push({ kind: ContextKind.Duration });
      }
      break;
    case FunctionCallBody:
      // In this case we are in the given situation:
      //       sum() or in rate()
      // with the cursor between the bracket. So we can autocomplete the metric, the function and the aggregation.
      result.push({ kind: ContextKind.MetricName }, { kind: ContextKind.Function }, { kind: ContextKind.Aggregation });
      break;
    case Neq:
      if (node.parent?.type.id === MatchOp) {
        result.push({ kind: ContextKind.MatchOp });
      } else if (node.parent?.type.id === BinaryExpr) {
        result.push({ kind: ContextKind.BinOp });
      }
      break;
    case EqlSingle:
    case EqlRegex:
    case NeqRegex:
    case MatchOp:
      result.push({ kind: ContextKind.MatchOp });
      break;
    case Pow:
    case Mul:
    case Div:
    case Mod:
    case Add:
    case Sub:
    case Eql:
    case Gte:
    case Gtr:
    case Lte:
    case Lss:
    case And:
    case Unless:
    case Or:
    case BinaryExpr:
      result.push({ kind: ContextKind.BinOp });
      break;
  }
  return result;
}

// HybridComplete provides a full completion result with or without a remote prometheus.
export class HybridComplete implements CompleteStrategy {
  private readonly prometheusClient: PrometheusClient | undefined;
  private readonly maxMetricsMetadata: number;
  private readonly enableBuiltInVariables: boolean;
  private readonly variables: VariableCompletion[];

  constructor(
    prometheusClient?: PrometheusClient,
    maxMetricsMetadata = 10000,
    enableBuiltInVariables = false,
    variables: VariableCompletion[] = []
  ) {
    this.prometheusClient = prometheusClient;
    this.maxMetricsMetadata = maxMetricsMetadata;
    this.enableBuiltInVariables = enableBuiltInVariables;
    this.variables = variables;
  }

  private buildVariableTerms(variables: VariableCompletion[], labelName?: string): Completion[] {
    if (variables.length === 0) {
      return [];
    }

    const normalizedContextLabel = normalizeLabelName(labelName);
    const exactLabelMatches: VariableCompletion[] = [];
    const fallbacks: VariableCompletion[] = [];

    for (const variable of variables) {
      const normalizedVariableLabel = normalizeLabelName(variable.labelName);
      if (
        normalizedContextLabel &&
        normalizedVariableLabel !== '' &&
        normalizedVariableLabel === normalizedContextLabel
      ) {
        exactLabelMatches.push(variable);
      } else {
        fallbacks.push(variable);
      }
    }

    const seen = new Set<string>();
    const toCompletion = (variable: VariableCompletion, boost: number): Completion | null => {
      const label = `$${variable.name}`;
      if (seen.has(label)) {
        return null;
      }
      seen.add(label);
      return { label, type: 'variable', boost };
    };

    const exactTerms = exactLabelMatches
      .sort(compareVariableCompletions)
      .map((variable) => toCompletion(variable, 200))
      .filter((term): term is Completion => term !== null);
    const fallbackTerms = fallbacks
      .sort(compareVariableCompletions)
      .map((variable) => toCompletion(variable, 50))
      .filter((term): term is Completion => term !== null);

    return [...exactTerms, ...fallbackTerms];
  }

  private getLabelNameForMatchOpContext(tree: SyntaxNode, state: EditorState): string {
    const labelMatcher = walkBackward(tree, LabelMatcher);
    const firstChild = labelMatcher?.firstChild;
    if (!firstChild) {
      return '';
    }
    if (firstChild.type.id === LabelName) {
      return state.sliceDoc(firstChild.from, firstChild.to);
    }
    if (firstChild.type.id === QuotedLabelName) {
      return unquoteLabelName(state.sliceDoc(firstChild.from, firstChild.to));
    }
    return '';
  }

  private buildMatchOpVariableTerms(labelName: string, matchOpPrefix: string): Completion[] {
    const contextLabel = unquoteLabelName(labelName).trim();
    if (!contextLabel) {
      return [];
    }

    const operators = matchOpPrefix.includes('!=~') ? ['=~', '!~'] : [matchOpPrefix.includes('!') ? '!~' : '=~'];
    const seen = new Set<string>();
    const terms: Completion[] = [];
    for (const variable of this.variables) {
      const variableLabel = unquoteLabelName(variable.labelName).trim();
      // For regex variable continuations, require strict label-name equality.
      if (!variableLabel || variableLabel !== contextLabel) {
        continue;
      }
      for (const operator of operators) {
        const completionLabel = `${operator}"$${variable.name}"`;
        if (seen.has(completionLabel)) {
          continue;
        }
        seen.add(completionLabel);
        terms.push({
          label: completionLabel,
          type: 'operator',
          boost: 300,
          apply: (view, _completion, from, to) => {
            const insertFrom = findMatchOpStart(view.state, from);
            const insertTo = findLabelValueEndAfterMatchOp(view.state, to);
            view.dispatch({
              changes: { from: insertFrom, to: insertTo, insert: completionLabel },
              selection: { anchor: insertFrom + completionLabel.length },
            });
          },
        });
      }
    }
    return terms;
  }

  private getVariablePrefixStart(state: EditorState, pos: number, stopChars: Set<string>): number {
    let prefixStart = pos;
    for (let i = pos - 1; i >= 0; i--) {
      const char = state.sliceDoc(i, i + 1);
      if (stopChars.has(char)) {
        break;
      }
      prefixStart = i;
    }
    return prefixStart;
  }

  promQL(context: CompletionContext): Promise<CompletionResult | null> | CompletionResult | null {
    const { state, pos } = context;
    const tree = syntaxTree(state).resolve(pos, -1);
    const contexts = analyzeNode(state, tree);
    let asyncResult: Promise<Completion[]> = Promise.resolve([]);
    let completeSnippet = false;
    let span = true;
    let filter = true;
    let customStartPos: number | undefined = undefined;
    for (const context of contexts) {
      switch (context.kind) {
        case ContextKind.Aggregation:
          completeSnippet = true;
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.aggregateOp ?? []);
          });
          break;
        case ContextKind.Function:
          completeSnippet = true;
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.functionIdentifier ?? []);
          });
          break;
        case ContextKind.BinOpModifier:
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.binOpModifier ?? []);
          });
          break;
        case ContextKind.BinOp:
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.binOp ?? []);
          });
          break;
        case ContextKind.MatchOp:
          asyncResult = asyncResult.then((result) => {
            const labelName = this.getLabelNameForMatchOpContext(tree, state);
            const matchOpStart = findMatchOpStart(state, pos);
            const matchOpText = state.sliceDoc(matchOpStart, findMatchOpEnd(state, pos));
            if (matchOpText.includes('!=~')) {
              filter = false;
            }
            const exactMatchVariableTerms = this.buildMatchOpVariableTerms(labelName, matchOpText);
            return result.concat(autocompleteNodes.matchOp ?? [], exactMatchVariableTerms);
          });
          break;
        case ContextKind.AggregateOpModifier:
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.aggregateOpModifier ?? []);
          });
          break;
        case ContextKind.Duration: {
          span = false;
          // Check if the user is typing a variable (starts with $)
          // Look backwards from cursor to find the start of what they're typing inside []
          const varPrefixStart = this.getVariablePrefixStart(state, pos, durationVariablePrefixStopChars);
          const typedText = state.sliceDoc(varPrefixStart, pos);

          if (typedText.startsWith('$')) {
            customStartPos = varPrefixStart;
            asyncResult = asyncResult.then((result) => {
              const variableTerms = this.buildVariableTerms(this.variables.filter(isDurationVariable)).filter((term) =>
                term.label.startsWith(typedText)
              );
              const builtInTerms = this.enableBuiltInVariables
                ? builtInVariableTerms.filter((term) => term.label.startsWith(typedText))
                : [];
              return result.concat(variableTerms, builtInTerms);
            });
          } else {
            asyncResult = asyncResult.then((result) => {
              return result.concat(autocompleteNodes.duration ?? []);
            });
          }
          break;
        }
        case ContextKind.Offset:
          asyncResult = asyncResult.then((result) => {
            return result.concat([{ label: 'offset' }]);
          });
          break;
        case ContextKind.Bool:
          asyncResult = asyncResult.then((result) => {
            return result.concat([{ label: 'bool' }]);
          });
          break;
        case ContextKind.AtModifiers:
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.atModifier ?? []);
          });
          break;
        case ContextKind.Number:
          asyncResult = asyncResult.then((result) => {
            return result.concat(autocompleteNodes.number ?? []);
          });
          break;
        case ContextKind.MetricName:
          // Use custom start position if provided (for metric names with special chars in LabelMatchers)
          if (context.startPos !== undefined) {
            customStartPos = context.startPos;
          }
          asyncResult = asyncResult.then((result) => {
            return this.autocompleteMetricName(result, context);
          });
          break;
        case ContextKind.LabelName:
          asyncResult = asyncResult.then((result) => {
            return this.autocompleteLabelName(result, context);
          });
          break;
        case ContextKind.LabelValue: {
          const varPrefixStart = this.getVariablePrefixStart(state, pos, labelValueVariablePrefixStopChars);
          const typedText = state.sliceDoc(varPrefixStart, pos);
          if (typedText.startsWith('$') && context.regexLabelValue) {
            span = false;
            customStartPos = varPrefixStart;
            asyncResult = asyncResult.then((result) => {
              const variableTerms = this.buildVariableTerms(
                this.variables.filter(isLabelValueVariable),
                context.labelName
              ).filter((term) => term.label.startsWith(typedText));
              return result.concat(variableTerms);
            });
          } else {
            asyncResult = asyncResult.then((result) => {
              const variableTerms =
                context.regexLabelValue && typedText === ''
                  ? this.buildVariableTerms(this.variables.filter(isLabelValueVariable), context.labelName)
                  : [];
              return this.autocompleteLabelValue(result.concat(variableTerms), context);
            });
          }
          break;
        }
      }
    }
    return asyncResult.then((result) => {
      const startPos = customStartPos !== undefined ? customStartPos : computeStartCompletePosition(tree, pos);
      return arrayToCompletionResult(result, startPos, pos, completeSnippet, span, filter);
    });
  }

  private autocompleteMetricName(result: Completion[], context: Context): Completion[] | Promise<Completion[]> {
    if (!this.prometheusClient) return result;

    const metricCompletion = new Map<string, Completion>();
    return this.prometheusClient
      .metricNames(context.metricName)
      .then((metricNames: string[]) => {
        if (context.shouldHideUTF8Metrics) {
          metricNames = metricNames.filter((metricName) => !metricHasUTF8Characters(metricName));
        }

        for (const metricName of metricNames) {
          if (context.quoted) {
            // Inside quotes (e.g. "")
            // Just insert the metric name.
            metricCompletion.set(metricName, { label: metricName, type: 'constant' });
          } else if (context.insideLabelMatchers) {
            // Inside label matchers (e.g. {})
            // Insert quoted metric name and a comma
            metricCompletion.set(metricName, {
              label: metricName,
              type: 'constant',
              apply: `${quoteString(metricName)}, `,
            });
          } else {
            // Outside label matchers (e.g. {})
            // If metric is UTF-8, wrap it in {"..."} to produce valid PromQL.
            // If metric is not UTF-8, just insert the metric name.
            metricCompletion.set(metricName, {
              label: metricName,
              type: 'constant',
              apply: createApplyForMetricName(metricName),
            });
          }
        }

        // avoid to get all metric metadata if the prometheus server is too big
        if (metricNames.length <= this.maxMetricsMetadata) {
          // in order to enrich the completion list of the metric,
          // we are trying to find the associated metadata
          return this.prometheusClient?.metricMetadata();
        }
      })
      .then((metricMetadata) => {
        if (metricMetadata) {
          for (const [metricName, node] of metricCompletion) {
            // For histograms and summaries, the metadata is only exposed for the base metric name,
            // not separately for the _count, _sum, and _bucket time series.
            const metadata = metricMetadata[metricName.replace(/(_count|_sum|_bucket)$/, '')];
            if (metadata) {
              if (metadata.length > 1) {
                // it means the metricName has different possible helper and type
                for (const m of metadata) {
                  if (node.detail === '') {
                    node.detail = m.type;
                  } else if (node.detail !== m.type) {
                    node.detail = 'unknown';
                    node.info = 'multiple different definitions for this metric';
                  }

                  if (node.info === '') {
                    node.info = m.help;
                  } else if (node.info !== m.help) {
                    node.info = 'multiple different definitions for this metric';
                  }
                }
              } else if (metadata.length === 1) {
                let { type, help } = metadata[0] ?? {};
                if (type === 'histogram' || type === 'summary') {
                  if (metricName.endsWith('_count')) {
                    type = 'counter';
                    help = `The total number of observations for: ${help}`;
                  }
                  if (metricName.endsWith('_sum')) {
                    type = 'counter';
                    help = `The total sum of observations for: ${help}`;
                  }
                  if (metricName.endsWith('_bucket')) {
                    type = 'counter';
                    help = `The total count of observations for a bucket in the histogram: ${help}`;
                  }
                }
                node.detail = type;
                node.info = help;
              }
            }
          }
        }
        const metricAutocomplete = Array.from(metricCompletion.values());

        // If there is a metadata client, we can add an async tooltip to the metric
        if (this.prometheusClient?.metadataClient) {
          metricAutocomplete.forEach((v) => {
            // Add an async tooltip to the metric
            v.info = async () => {
              // Create a DOM that will be used as placeholder for the metric info
              const el = document.createElement('div');
              el.innerHTML = 'Loading metadata...';

              // Boolean that keeps track if the tooltip is still present
              let isPresent = true;
              // Load the metric info asynchronously
              const asyncLoadMetricInfo = async () => {
                // wait 300ms as a way to debounce the request
                await new Promise((resolve) => setTimeout(resolve, 300));
                // Only send the request if the tooltip is still present
                if (!isPresent || !this.prometheusClient?.metadataClient) {
                  return;
                }
                const r = await this.prometheusClient.metadataClient.getMetricInfo(v.label);
                const metadataEl = getMetadataTooltipDOM({
                  metricName: v.label,
                  type: r?.type ?? undefined,
                  description: r?.description ?? undefined,
                  descriptionSource: r?.descriptionSource ?? undefined,
                  subType: r?.subType ?? undefined,
                });

                // Replace the loading dom with the metric info dom
                el.replaceWith(metadataEl);
              };
              asyncLoadMetricInfo();

              return {
                dom: el,
                destroy: () => {
                  // Set the boolean to false to avoid sending the request if the tooltip is not longer present
                  isPresent = false;
                },
              };
            };
          });
        }
        return result.concat(metricAutocomplete);
      });
  }

  private autocompleteLabelName(result: Completion[], context: Context): Completion[] | Promise<Completion[]> {
    if (!this.prometheusClient) {
      return result;
    }

    // Remove __name__ matcher if metric name was extracted from it
    const matchers = context.matchers?.filter((m) => m.name !== '__name__') ?? [];

    return this.prometheusClient
      .labelNames(context.metricName, removeMatchersThatContainVariables(matchers))
      .then((labelNames: string[]) => {
        const filteredLabelNames = labelNames.filter((labelName) => {
          // Filter out '__name__' from results if a metric name is already defined.
          if (context.metricName && labelName === '__name__') {
            return false;
          }
          return true;
        });

        return result.concat(
          filteredLabelNames.map((value) => {
            // If we're already in a quoted context (QuotedLabelName), just insert the value
            // Otherwise, if the label name needs quoting, wrap it in quotes
            return {
              label: value,
              type: 'constant',
              apply: !context.quoted && labelNameHasUTF8Characters(value) ? quoteString(value) : undefined,
            };
          })
        );
      });
  }

  private autocompleteLabelValue(result: Completion[], context: Context): Completion[] | Promise<Completion[]> {
    if (!this.prometheusClient || !context.labelName) {
      return result;
    }

    return this.prometheusClient
      .labelValues(context.labelName, context.metricName, removeMatchersThatContainVariables(context.matchers))
      .then((labelValues: string[]) => {
        return result.concat(labelValues.map((value) => ({ label: value, type: 'text' })));
      });
  }
}

/**
 * Remove matchers that contain variables.
 * TODO: Right now its not perfect, eventually we should use the parser to remove the matchers that contain variables.
 * e.g. It's possible $ lives in the label value and not a variable.
 */
function removeMatchersThatContainVariables(matchers: Matcher[] | undefined) {
  if (!matchers) {
    return matchers;
  }
  return matchers?.filter((matcher) => {
    return !matcher.value.includes('$');
  });
}

/**
 * When inserting a UTF-8 metric name, we need to wrap it in {"..."}, add a comma, and position the cursor after the comma.
 */
const createApplyForMetricName = (metricName: string) => {
  const isUTF8MetricName = metricHasUTF8Characters(metricName);
  const modifiedMetricName = isUTF8MetricName ? `{${quoteString(metricName)}, }` : metricName;
  return (view: EditorView, _completion: Completion, from: number, to: number) => {
    view.dispatch({
      changes: { from, to, insert: modifiedMetricName },
      selection: { anchor: from + modifiedMetricName.length - (isUTF8MetricName ? 1 : 0) },
    });
  };
};
